import type { RoomPlayerHistoryEventView, RoomView } from "../../shared/game";
import { localizeModeLabel, t } from "./i18n";
import { renderZiarcadiaIcon } from "./ziarcadiaIcons";

const PLAYER_HISTORY_DOWNLOAD_MIME = "text/plain;charset=utf-8";
const PLAYER_HISTORY_PREVIEW_LIMIT = 6;

const escapeHtml = (value: string): string =>
    value.replace(/[&<>"']/g, (character) => {
        switch (character) {
            case "&":
                return "&amp;";
            case "<":
                return "&lt;";
            case ">":
                return "&gt;";
            case '"':
                return "&quot;";
            case "'":
                return "&#39;";
            default:
                return character;
        }
    });

const formatFileDate = (date = new Date()): string =>
    date.toISOString().replace(/[:.]/g, "-").slice(0, 19);

const formatEventTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);

    return Number.isNaN(date.getTime()) ? "unknown time" : date.toISOString();
};

const quote = (value: string | null | undefined): string =>
    value ? `"${value.replace(/"/g, '\\"')}"` : "none";

const getEventLabel = (event: RoomPlayerHistoryEventView): string => {
    switch (event.kind) {
        case "join":
            return "JOIN";
        case "reconnect":
            return "RECONNECT";
        case "rename":
            return "RENAME";
        case "rename-request":
            return "REDO REQUEST";
        case "disconnect":
            return "DISCONNECT";
        case "kick":
            return "KICK";
        default:
            return "EVENT";
    }
};

const getPreviewEventLabel = (event: RoomPlayerHistoryEventView): string => {
    switch (event.kind) {
        case "join":
            return t("playerHistoryJoined");
        case "reconnect":
            return t("playerHistoryReconnected");
        case "rename":
            return t("playerHistoryRenamed");
        case "rename-request":
            return t("playerHistoryRenameRequested");
        case "disconnect":
            return t("playerHistoryDisconnected");
        case "kick":
            return t("playerHistoryKicked");
        default:
            return t("playerHistoryEvent");
    }
};

const getPreviewEventClass = (event: RoomPlayerHistoryEventView): string => {
    switch (event.kind) {
        case "join":
            return "roster-history-event is-join";
        case "reconnect":
            return "roster-history-event is-reconnect";
        case "rename":
            return "roster-history-event is-rename";
        case "rename-request":
            return "roster-history-event is-rename-request";
        case "disconnect":
            return "roster-history-event is-disconnect";
        case "kick":
            return "roster-history-event is-kick";
        default:
            return "roster-history-event is-event";
    }
};

const getDisplayName = (event: RoomPlayerHistoryEventView): string =>
    event.nameAfter?.trim() ||
    event.inputName?.trim() ||
    event.nameBefore?.trim() ||
    event.actorName?.trim() ||
    event.sessionId;

const getEventDetail = (event: RoomPlayerHistoryEventView): string => {
    const before = event.nameBefore?.trim();
    const after = event.nameAfter?.trim();

    if (event.kind === "rename" && before && after && before !== after) {
        return `${before} -> ${after}`;
    }

    if (event.kind === "rename-request" && event.actorName?.trim()) {
        return event.actorName.trim();
    }

    return getDisplayName(event);
};

const getPreviewEventDetail = (event: RoomPlayerHistoryEventView): string | null => {
    const detail = getEventDetail(event).trim();
    const displayName = getDisplayName(event).trim();

    return detail && detail !== displayName ? detail : null;
};

const renderPlayerHistoryPreview = (room: RoomView): string => {
    const events = (room.playerHistory ?? []).slice(-PLAYER_HISTORY_PREVIEW_LIMIT).reverse();

    if (events.length === 0) {
        return `<span class="roster-history-empty">${escapeHtml(t("playerHistoryEmpty"))}</span>`;
    }

    return `
        <span class="roster-history-list" role="list">
            ${events
                .map((event) => {
                    const detail = getPreviewEventDetail(event);

                    return `
                        <span class="${escapeHtml(getPreviewEventClass(event))}" role="listitem">
                            <span class="roster-history-event-kind">${escapeHtml(getPreviewEventLabel(event))}</span>
                            <span class="roster-history-event-name">${escapeHtml(getDisplayName(event))}</span>
                            ${
                                detail
                                    ? `<span class="roster-history-event-detail">${escapeHtml(detail)}</span>`
                                    : ""
                            }
                        </span>
                    `;
                })
                .join("")}
        </span>
    `;
};

const buildEventLine = (event: RoomPlayerHistoryEventView, index: number): string => {
    const parts = [
        `${String(index + 1).padStart(3, "0")}. ${formatEventTimestamp(event.recordedAt)}`,
        getEventLabel(event),
        `session=${event.sessionId}`,
        `role=${event.role}`,
        `before=${quote(event.nameBefore)}`,
        `after=${quote(event.nameAfter)}`
    ];

    if (event.inputName) {
        parts.push(`input=${quote(event.inputName)}`);
    }

    if (event.actorSessionId || event.actorName) {
        parts.push(`actor=${quote(event.actorName)}`);
        parts.push(`actorSession=${event.actorSessionId ?? "none"}`);
    }

    return parts.join(" | ");
};

export const buildPlayerHistoryReport = (room: RoomView, downloadedAt = new Date()): string => {
    const history = room.playerHistory ?? [];
    const modeLabel = localizeModeLabel(room.mode) ?? room.mode ?? "Unselected";
    const lines = [
        "Ziarcadia player history",
        `Room: ${room.roomCode}`,
        `Mode: ${modeLabel}`,
        `Downloaded: ${downloadedAt.toISOString()}`,
        `Events: ${history.length}`,
        ""
    ];

    if (history.length === 0) {
        lines.push("No player history events were recorded for this room.");
    } else {
        lines.push(...history.map(buildEventLine));
    }

    return `${lines.join("\n")}\n`;
};

export const getPlayerHistoryDownloadFilename = (room: RoomView, date = new Date()): string =>
    `ziarcadia-${room.roomCode.toLowerCase()}-player-history-${formatFileDate(date)}.txt`;

export const downloadPlayerHistory = (room: RoomView) => {
    if (!room.viewer.isHost) {
        return;
    }

    const blob = new Blob([buildPlayerHistoryReport(room)], {
        type: PLAYER_HISTORY_DOWNLOAD_MIME
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = getPlayerHistoryDownloadFilename(room);
    document.body.append(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

type PlayerHistoryHeaderMenuOptions = {
    className?: string;
    countClassName?: string;
    open?: boolean;
};

export const renderPlayerHistoryHeaderMenu = (
    room: RoomView,
    count: string,
    options: PlayerHistoryHeaderMenuOptions = {}
): string => {
    if (!room.viewer.isHost) {
        return `<strong class="${escapeHtml(
            ["roster-count", "roster-history-count", options.countClassName].filter(Boolean).join(" ")
        )}">${escapeHtml(count)}</strong>`;
    }

    const menuLabel = t("playerListOptions");
    const downloadLabel = t("downloadPlayerHistory");
    const menuClassAttribute = ["roster-history-menu", options.className, options.open ? "is-open" : ""]
        .filter(Boolean)
        .join(" ");
    const triggerClassAttribute = ["roster-count", "roster-history-trigger", options.countClassName]
        .filter(Boolean)
        .join(" ");

    return `
        <span class="${escapeHtml(menuClassAttribute)}" data-player-history-menu>
            <button
                class="${escapeHtml(triggerClassAttribute)}"
                type="button"
                data-player-history-menu-toggle
                aria-haspopup="dialog"
                aria-expanded="${options.open ? "true" : "false"}"
                aria-label="${escapeHtml(`${menuLabel}: ${count}`)}"
                title="${escapeHtml(menuLabel)}"
            >${escapeHtml(count)}</button>
            <span class="roster-history-popover" role="dialog" aria-label="${escapeHtml(menuLabel)}"${
                options.open ? "" : " hidden"
            }>
                ${renderPlayerHistoryPreview(room)}
                <button
                    class="roster-history-download-button"
                    type="button"
                    data-player-history-download
                    aria-label="${escapeHtml(downloadLabel)}"
                    title="${escapeHtml(downloadLabel)}"
                >
                    ${renderZiarcadiaIcon("download", { className: "roster-history-icon" })}
                </button>
            </span>
        </span>
    `;
};
